<template>
  <div class="collections-container">
    <h1>{{ t('collections.list.title') }}</h1>

    <div class="actions-bar">
      <router-link to="/collections/create" class="btn btn-primary">
        {{ t('collections.list.buttons.create') }}
      </router-link>
    </div>

    <div v-if="isLoading" class="loading-state">
      <p>{{ t('collections.list.loading') }}</p>
      <!-- Add a spinner or more elaborate loading indicator here if desired -->
      <div class="spinner"></div>
    </div>

    <div v-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button @click="fetchCollections" class="btn btn-secondary">{{ t('common.retry') }}</button>
    </div>

    <div v-if="!isLoading && !error && collections.length === 0" class="empty-state">
      <p>{{ t('collections.list.empty.title') }}</p>
      <p>{{ t('collections.list.empty.subtitle') }}</p>
    </div>

    <div v-if="!isLoading && !error && collections.length > 0" class="collections-list">
      <!-- Using a simple list structure for now. Can be replaced with Table.vue if its API is known -->
      <table>
        <thead>
          <tr>
            <th>{{ t('collections.list.table.name') }}</th>
            <th>{{ t('collections.list.table.assetCount') }}</th>
            <th>{{ t('collections.list.table.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="collection in collections" :key="collection.id">
            <td>{{ collection.name }}</td>
            <td>{{ collection.assets ? collection.assets.length : 0 }}</td>
            <td class="collection-actions">
              <router-link :to="`/collections/${collection.id}`" class="btn btn-sm btn-info">{{ t('collections.list.table.view') }}</router-link>
              <router-link :to="`/collections/edit/${collection.id}`" class="btn btn-sm btn-warning">{{ t('collections.list.table.edit') }}</router-link>
              <button @click="confirmDeleteCollection(collection.id)" class="btn btn-sm btn-danger">{{ t('collections.list.table.delete') }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCollections as apiGetCollections, deleteCollection as apiDeleteCollection } from '@/services/api.js';

/**
 * @file collections.vue
 * @description Page component for listing, viewing, and managing user's asset collections.
 * It allows users to see their created collections, navigate to create new ones,
 * view details, edit, or delete existing collections.
 */

/**
 * Reactive array holding the list of user's collections.
 * Each collection object typically contains `id`, `name`, and `assetCount`.
 * @type {import('vue').Ref<Array<object>>}
 */
const collections = ref([]);

/**
 * Reactive boolean to indicate if collections data is currently being loaded.
 * @type {import('vue').Ref<boolean>}
 */
const isLoading = ref(false);

/**
 * Reactive ref to store any error message that occurs during API calls.
 * @type {import('vue').Ref<string|null>}
 */
const error = ref(null);
const { t } = useI18n();

/**
 * Fetches collections from the API (mocked) and updates the component's reactive state.
 * Handles loading and error states during the fetch operation.
 * @async
 */
const fetchCollections = async () => {
  isLoading.value = true;
  error.value = null;
  try {
    // Use the imported (or soon-to-be-implemented) API function
    const response = await apiGetCollections();
    // Assuming the API returns the array directly or an object with a data property
    collections.value = Array.isArray(response) ? response : (response?.data || []);
    if (collections.value.length === 0 && !Array.isArray(response) && !response?.data) {
        // This handles cases where apiGetCollections might return an empty object or undefined
        // when it should be an array.
        console.warn("fetchCollections: Received non-array response or no data field, defaulting to empty array.", response);
    }
  } catch (err) {
    console.error('Error fetching collections:', err);
    const reason = err?.message || t('common.unknownError');
    error.value = t('collections.list.errors.loadFailed', { reason });
  } finally {
    isLoading.value = false;
  }
};

/**
 * Handles the deletion of a specific collection after user confirmation via a dialog.
 * On successful deletion, it updates the local `collections` list.
 * On failure, it sets an error message and alerts the user.
 * @async
 * @param {string} collectionId - The unique identifier of the collection to be deleted.
 */
const confirmDeleteCollection = async (collectionId) => {
  if (window.confirm(t('collections.list.prompts.deleteConfirm'))) {
    isLoading.value = true; // Optional: indicate global loading state for the action
    try {
      const response = await apiDeleteCollection(collectionId);
      if (response.success) {
        // Remove the collection from the local list for immediate UI update
        collections.value = collections.value.filter(col => col.id !== collectionId);
        alert(t('collections.list.messages.deleteSuccess')); // Simple feedback
      } else {
        // API returned success: false
        throw new Error(response.message || t('collections.list.errors.deleteUnknown'));
      }
    } catch (err) {
      console.error(`Error deleting collection ${collectionId}:`, err);
      const reason = err?.message || t('common.unknownError');
      error.value = t('collections.list.errors.deleteFailed', { reason }); // Display error in the error-state div
      alert(error.value); // Also show an alert for immediate feedback on action
    } finally {
      isLoading.value = false;
    }
  }
};

// Fetches the initial list of collections when the component is mounted.
onMounted(() => {
  fetchCollections();
});
</script>

<style scoped>
.collections-container {
  padding: 20px;
  max-width: 900px;
  margin: 0 auto;
  font-family: Arial, sans-serif;
}

h1 {
  text-align: center;
  margin-bottom: 20px;
  color: #333;
}

.actions-bar {
  margin-bottom: 20px;
  text-align: right;
}

.btn {
  padding: 8px 15px;
  border-radius: 4px;
  text-decoration: none;
  cursor: pointer;
  font-size: 0.9em;
  border: 1px solid transparent;
  margin-left: 5px; /* For spacing buttons in a row */
}

.btn-primary {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}
.btn-primary:hover {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
  border-color: #6c757d;
}

.btn-info {
  background-color: #17a2b8;
  color: white;
}
.btn-warning {
  background-color: #ffc107;
  color: #212529;
}
.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-sm {
  padding: 5px 10px;
  font-size: 0.8em;
}


.loading-state, .error-state, .empty-state {
  text-align: center;
  padding: 40px 0;
  color: #555;
}

.error-message {
  color: #dc3545;
  margin-bottom: 10px;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border-left-color: #007bff;
  animation: spin 1s ease infinite;
  margin: 20px auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.collections-list table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.collections-list th, .collections-list td {
  border: 1px solid #ddd;
  padding: 10px 12px;
  text-align: left;
}

.collections-list th {
  background-color: #f8f9fa;
  font-weight: bold;
  color: #333;
}

.collections-list tr:nth-child(even) {
  background-color: #f2f2f2;
}

.collections-list tr:hover {
  background-color: #e9ecef;
}

.collection-actions .btn {
  margin-right: 5px; /* Spacing between action buttons */
}
.collection-actions .btn:last-child {
  margin-right: 0;
}

</style>
